import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLaunchParams } from "@telegram-apps/sdk-react";
import { ChevronsUpDown, Search } from "lucide-react";
import { useUserExchanges } from "@/hooks/useExchanges";
import { NotificationsBell } from "@/components/notifications/NotificationsBell";

export interface DesktopSearch {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

/**
 * Desktop header from the redesign: search, Telegram, notifications,
 * profile and the exchange connect button.
 */
export const DesktopTopBar: React.FC<{ search?: DesktopSearch }> = ({ search }) => {
  const navigate = useNavigate();
  const user = useLaunchParams().initData?.user;
  const userId = user?.id?.toString();
  const { data: exchanges } = useUserExchanges(userId);
  const [localQuery, setLocalQuery] = useState("");
  const bot = import.meta.env.VITE_TELEGRAM_BOT_USERNAME || "triggerdotfun_bot";

  const name = [user?.firstName, user?.lastName].filter(Boolean).join(" ") || "Trader";
  const initials = name
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("");
  const connected = exchanges?.length ?? 0;

  return (
    <div className="flex items-center justify-between gap-6">
      <label className="glass flex h-12 w-80 items-center gap-2 px-3.5">
        <Search className="size-5 shrink-0 text-[#a3a3a3]" />
        <input
          value={search ? search.value : localQuery}
          onChange={(e) => (search ? search.onChange(e.target.value) : setLocalQuery(e.target.value))}
          onKeyDown={(e) => {
            if (!search && e.key === "Enter" && localQuery.trim()) {
              navigate(`/analyze?symbol=${encodeURIComponent(localQuery.trim().toUpperCase())}`);
            }
          }}
          placeholder={search?.placeholder ?? "Search assets, exchanges..."}
          className="min-w-0 flex-1 bg-transparent text-base leading-6 text-white outline-none placeholder:text-[#737373]"
        />
      </label>

      <div className="flex items-center gap-6">
        <div className="flex items-center gap-2">
          <a
            href={`https://t.me/${bot}`}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Open the Telegram bot"
            className="glass flex items-center justify-center p-2"
          >
            <img src="/icons/figma/telegram.svg" alt="" width={20} height={20} />
          </a>
          <NotificationsBell />
        </div>

        <button
          type="button"
          onClick={() => navigate("/settings")}
          className="flex items-center gap-1 text-left"
        >
          <span className="flex items-center gap-2">
            <span className="flex size-12 items-center justify-center overflow-hidden rounded-full border border-[#00c27e] bg-[#262626] text-base font-semibold text-[#a3a3a3]">
              {user?.photoUrl ? (
                <img src={user.photoUrl} alt="" className="size-full object-cover" />
              ) : (
                initials || "?"
              )}
            </span>
            <span className="flex flex-col text-base leading-6">
              <span className="font-semibold text-white">{name}</span>
              {user?.username && <span className="text-[#a3a3a3]">@{user.username}</span>}
            </span>
          </span>
          <ChevronsUpDown className="size-4 text-[#a3a3a3]" />
        </button>

        <button
          type="button"
          aria-label="Exchange settings"
          onClick={() => navigate("/settings")}
          className="rounded-xl border-2 border-white/10 bg-[#07a66c] px-[18px] py-3 text-base font-semibold leading-6 text-white hover:bg-[#08b877]"
        >
          {connected > 0
            ? `${connected} Exchange${connected === 1 ? "" : "s"} Connected`
            : "Connect Exchange"}
        </button>
      </div>
    </div>
  );
};
